/**
 * Service Registry - Centralized service instances
 * 
 * This module provides a single point of access to all core services.
 * Services are lazily instantiated and cached as singletons.
 * 
 * Benefits:
 * - Clear dependency visualization
 * - Single source of truth for service instances
 * - Enables future dependency injection patterns
 * - Simplifies testing (can mock entire registry)
 */

import { connectionStateService } from './infrastructure/connection-state-service'
import { appLifecycleService } from './infrastructure/app-lifecycle-service'
import { commandExecutor } from './commands/executor'
import { eventProcessor } from './events/processor'
import { memoryStateService } from './state/memory-store'
import { tabSyncCoordinator } from './coordination/tab-sync-coordinator'
import { scenarioHydrationCoordinator } from './coordination/scenario-hydration-coordinator'
import { serverSyncService } from './infrastructure/server-sync-service'
import { leaderElection } from '../api/leader-election'

/**
 * Core Services - Available to all layers
 */
export const services = {
  // Infrastructure Layer - No dependencies
  connectionState: connectionStateService,
  lifecycle: appLifecycleService,

  // Domain Layer - Pure business logic
  commandExecutor: commandExecutor,
  eventProcessor: eventProcessor,
  
  // State Layer - Projected view
  memoryState: memoryStateService,
  
  // Coordination Layer - Orchestration
  tabSync: tabSyncCoordinator,
  hydrationCoordinator: scenarioHydrationCoordinator,
  
  // Server Layer - External communication
  serverSync: serverSyncService,
} as const

/**
 * Initialize all services in the correct order
 * 
 * Should be called once at application startup.
 * Uses AppLifecycleService for proper initialization sequencing.
 */
export async function initializeServices(): Promise<void> {
  // Register services in dependency order
  // 1. Foundation: Network & Leader Election
  appLifecycleService.registerService(connectionStateService)
  appLifecycleService.registerService(leaderElection)
  
  // 2. Coordination
  appLifecycleService.registerService(tabSyncCoordinator)

  // NOTE: User-specific services (ServerSync, Realtime) are initialized by
  // UserContextService only after a user is authenticated.

  await appLifecycleService.initialize()
}

// Export individual services for backward compatibility
export { connectionStateService } from './infrastructure/connection-state-service'
export { appLifecycleService } from './infrastructure/app-lifecycle-service'
export { commandExecutor } from './commands/executor'
export { eventProcessor } from './events/processor'
export { memoryStateService } from './state/memory-store'
export { tabSyncCoordinator } from './coordination/tab-sync-coordinator'
export { scenarioHydrationCoordinator } from './coordination/scenario-hydration-coordinator'
export { serverSyncService } from './infrastructure/server-sync-service'
